import type { SchedulerSettings } from "./settings";
import {
  type PersistedSettingKey,
  SETTINGS_VALIDATORS
} from "./settings-document";

/**
 * Which settings keys the store carries, and what a document still has left to offer
 * (ENG-2047 §6.3).
 *
 * Pure and store-free, because two very different callers need the same answers: the slice
 * decides whether a change is worth persisting, and the sync hook decides whether the late
 * document has anything the user has not already done by hand.
 */

/** Every key with a validator — so `slotMinutes` is absent, and cannot be persisted. */
export const PERSISTED_SETTING_KEYS = Object.keys(
  SETTINGS_VALIDATORS
) as PersistedSettingKey[];

export const isPersistedSettingKey = (
  key: string
): key is PersistedSettingKey =>
  Object.prototype.hasOwnProperty.call(SETTINGS_VALIDATORS, key);

/**
 * Element-wise for arrays, because `staffListIds` IS the column order: the same people in
 * a different order is a different setting, and reference equality would call every
 * freshly-read array a change.
 */
export const sameSetting = (a: unknown, b: unknown): boolean => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((value, at) => value === b[at]);
  }
  return a === b;
};

export interface NoticeApplicableInput {
  /** The validated keys the document carried. Keys it did not carry are not on offer. */
  values: Partial<SchedulerSettings>;
  current: SchedulerSettings;
  /** Keys the user changed this session; the document must never undo one of them. */
  touched: readonly PersistedSettingKey[];
  /**
   * The `scheduling-v3-radar` flag (`selectRadarEnabled`). ENG-2104's `radarOpen` is on
   * offer only while it is on: with it off the rail does not exist, so a stored `radarOpen`
   * would raise a notice whose Apply changes nothing on screen. Absent means off.
   */
  radarEnabled?: boolean;
}

/**
 * What Apply would actually change: a key the document carries, that differs from what is
 * on screen, and that the user has not touched since the page opened.
 *
 * Empty means the notice has nothing to offer and removes itself — a user who ignores it
 * and then hand-changes everything it was offering never has to dismiss it.
 */
export function noticeApplicableKeys({
  values,
  current,
  touched,
  radarEnabled = false
}: NoticeApplicableInput): PersistedSettingKey[] {
  const held = new Set<string>(touched);
  return PERSISTED_SETTING_KEYS.filter(
    (key) =>
      (key !== "radarOpen" || radarEnabled) &&
      key in values &&
      !held.has(key) &&
      !sameSetting(values[key], current[key])
  );
}
